import { ValueOpt } from "best-common-react-3";
import { saveGameTech, patchGameTech, deleteGameTechById } from "../../api/GameSettingApi";
import { DashboardConstants, DashboardType } from "../../constants/DashboardConstants";
import { getGameTechTypes } from "../../constants/GameTechConstants";
import { PositionConstants } from "../../constants/PositionConstants";
import { sortAlphabetic } from "../../contexts/DropdownContext";
import { DailyPositionAssignment, GameDataDTO } from "../../types/DailyAssignmentTypes";
import { User } from "../../types/SettingsTypes";
import { GameTech } from "../../types/CamTypes";
import { getPatch } from "./Patch";
import { getReplayOperatorOption } from "./ValueOptHelper";

export type GameTechRow = Partial<GameTech>;

const POST_SEASON_GAME_TYPES = ["D", "F", "L", "W"] as const;

export const isPostSeasonGameType = (gameType: string): boolean =>
  POST_SEASON_GAME_TYPES.includes(gameType as typeof POST_SEASON_GAME_TYPES[number]);

// TODO: This suffix mapping is bad practice - the backend GameTech entity has hardcoded
// dashboard-specific fields (helpfulnessOp, helpfulnessSupport, etc.) instead of a
// normalized design with a separate GAME_TECH_RATING table that stores:
// { gameTechId, dashboardType, helpfulness, competency, gradedBy }
// This would eliminate the need for suffix mapping and allow adding new dashboards
// without schema changes. For now, we map to match the legacy backend field names.
export const FIELD_SUFFIX: Partial<Record<DashboardType, string>> = {
  Operator: "Op", // Backend uses "Op" instead of "Operator"
};

export const getFieldKeys = (dashboardType: DashboardType) => {
  const suffix = FIELD_SUFFIX[dashboardType] ?? dashboardType;
  return {
    helpfulness: `helpfulness${suffix}`,
    competency: `competency${suffix}`,
    // Support uses "gradedBy" without suffix, others use "gradedBy{Suffix}"
    gradedBy: dashboardType === "Support" ? "gradedBy" : `gradedBy${suffix}`,
  };
};

const filterStaffAssignments = (dashboardType: DashboardType, staffAssignment: DailyPositionAssignment): boolean => {
  if (dashboardType === DashboardConstants.FLOATER.value) {
    return [
      PositionConstants.ISO,
      PositionConstants.MARKER,
      PositionConstants.HFR,
      PositionConstants.MOSAIC,
      PositionConstants.CAPTAIN,
    ].includes(staffAssignment.actualPosition);
  }
  return staffAssignment.actualPosition.includes(PositionConstants.SUPPORT);
};

export const getGradedByOptions = (
  staffAssignments: DailyPositionAssignment[],
  dashboardType: DashboardType,
): ValueOpt<any>[] => {
  const idSet: Set<number> = new Set<number>();
  const opts: ValueOpt<any>[] = [];
  staffAssignments
    ?.filter(s => filterStaffAssignments(dashboardType, s))
    .forEach(s => {
      ["dayUser", "nightUser", "lateNightUser"].forEach(property => {
        const user: User = s[property];
        if (user && !idSet.has(Number(user.id))) {
          idSet.add(Number(user.id));
          opts.push(getReplayOperatorOption(user));
        }
      });
    });
  return opts.sort(sortAlphabetic);
};

export const getGameTechRow = (techs: GameTech[], type: string): GameTechRow => {
  const tech: GameTech = techs?.find(gt => gt.type === type);
  return tech || { type };
};

export const getGameTechRows = (techs: GameTech[], gameType: string): GameTechRow[] => {
  return getGameTechTypes(isPostSeasonGameType(gameType)).map(
    (type: string): GameTechRow => getGameTechRow(techs, type),
  );
};

export const buildSaveHandler = (selectedGame: GameDataDTO) => (
  gameTech: GameTechRow,
  selected: any,
  property: string,
): void => {
  if (property === "tech" && !selected) {
    deleteGameTechById(gameTech.id);
  } else if (gameTech?.id) {
    patchGameTech(gameTech.id, getPatch(Boolean(gameTech[property]), `/${property}`, selected));
  } else {
    saveGameTech({
      tech: selected,
      type: gameTech.type,
      gamePk: Number(selectedGame?.gamePk),
    });
  }
};
